import { useState } from "react";
import {
  usePaginatedQuery,
  type PaginatedDataHookResult,
  type PaginatedFilters,
  type UsePaginatedQueryOptions,
  type UsePaginatedQueryResult,
} from "@marekl/adminer/hooks";
import { formatPageRangeSummary } from "@marekl/adminer/components";

const DEFAULT_LIMIT_OPTIONS = [10, 25, 50, 100];

export interface UsePagedListOptions extends UsePaginatedQueryOptions {
  /**
   * Noun forms for the footer summary, e.g. `["message", "messages",
   * "messages"]` (the [one, few, many] tuple shape is the lib's).
   */
  nounForms: readonly [one: string, few: string, many: string];
}

export interface UsePagedListResult<TItem> extends UsePaginatedQueryResult<TItem> {
  /**
   * "Showing X–Y of Z <noun>" against the backend's `total_estimate`.
   * Undefined while nothing is loaded or when the list is empty.
   */
  summary: string | undefined;
}

/**
 * `usePaginatedQuery` plus a page-range footer summary. The lib hook owns the
 * cursor + limit URL state; this wrapper additionally tracks the 1-based page
 * index (the cursor protocol doesn't carry one) so the footer can show
 * "Showing X–Y of Z". Like the lib's own back-stack, the index lives in
 * component state and is lost on hard refresh — acceptable.
 */
export function usePagedList<TFilters, TItem>(
  useDataHook: (filters: TFilters & PaginatedFilters) => PaginatedDataHookResult<TItem>,
  filters: TFilters,
  options: UsePagedListOptions,
): UsePagedListResult<TItem> {
  const { nounForms, limitOptions = DEFAULT_LIMIT_OPTIONS, ...rest } = options;
  const result = usePaginatedQuery(useDataHook, filters, { ...rest, limitOptions });
  const [page, setPage] = useState(1);

  const { pagination } = result;
  const total = result.data?.total_estimate ?? 0;
  const summary = formatPageRangeSummary(
    {
      total,
      page,
      perPage: pagination.limit,
      totalPages: pagination.limit > 0 ? Math.ceil(total / pagination.limit) : 0,
    },
    { nounForms },
  );

  return {
    ...result,
    pagination: {
      ...pagination,
      onNext: () => {
        if (!pagination.hasNext) return;
        setPage((p) => p + 1);
        pagination.onNext();
      },
      onPrev: () => {
        setPage((p) => Math.max(1, p - 1));
        pagination.onPrev();
      },
      onLimitChange: (n) => {
        setPage(1);
        pagination.onLimitChange(n);
      },
    },
    resetPagination: () => {
      setPage(1);
      result.resetPagination();
    },
    filterReset: (updater) => {
      setPage(1);
      result.filterReset(updater);
    },
    summary,
  };
}
